import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Icon,
  Spinner,
  Switch,
  Table,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { cloneDeep } from "lodash";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { IconType } from "react-icons";
import { FaArrowDown, FaArrowUp, FaDownload, FaEye } from "react-icons/fa";
import {
  BseDto,
  NegotiationRoundDto,
  ReceivedBseBidDto,
  useCompleteEvaluationMutation,
  useCompleteNegotiationRoundEvaluationMutation,
  useListReceivedBidsQuery,
  useOpenCompletedEventMutation,
  useOpenNegotiationRoundMutation,
} from "../../../../../autogen/bff-api";
import { ConversationModal, Message } from "../../../../../common/ConversationModal";
import { useLoggedInWithOrgContextState } from "../../../../../common/auth/useLoggedInWithOrgContextState";
import { useAppDispatch } from "../../../../../common/redux/hooks";
import {
  setNegotiationRound,
  setSourcingEvent,
} from "../../../../../common/redux/reducers/basicSourcingEventReducer";
import { updateProductGroup } from "../../../../../common/redux/reducers/productReducer";
import { editProductGroupThunk } from "../../../../../common/redux/thunks/product/editProductGroupThunk";
import { Product, ProductDraft, ProductGroup } from "../../../../../common/types";

import {
  calculateChangeSinceOriginalBid,
  getBidPercentChangeSinceOriginal,
} from "../../../../../common/products/productUtils";
import { requireBooleanEnvVar } from "../../../../../config";
import { eventIsAwarded } from "../../../eventIsAwarded";
import { eventIsCancelled } from "../../../eventIsCancelled";
import { eventIsClosed } from "../../../eventIsClosed";
import { eventIsCompleted } from "../../../eventIsCompleted";
import { useProductGroups } from "../../common/useProductGroups";
import { useNegotiation } from "../NegotiationContext";
import { ViewBidAsBuyerModal } from "../ViewBidAsBuyerModal";
import {
  getQuotesDelivered,
  getQuotesDeliveredInBid,
  getQuotesRequested,
  getSelectedOrWinningBids,
  getSumOfBestPrices,
  getSumOfSelectedPrices,
  getTotalPrice,
  isBestTotalPrice,
} from "./BidEvaluationUtils";
import { BidSelectionSaveStatus } from "./BidSelectionSaveStatus";
import { EvaluationModel, EvaluationModelType, EvaluationResult } from "./EvaluationModels";
import { EvaluationTable } from "./EvaluationTable";
import { AIEvaluator } from "./components/AIEvaluator";
import { EvaluationResults } from "./components/EvaluationResults";
import { ModelSelector } from "./components/ModelSelector";
import { generatePriceComparisonReport } from "./priceComparisonReport";
import { generateReport } from "./reportGenerator";
import { BidSelectionProvider, useSelectProductBids } from "./useSelectProductBids";

const isDeadlinePassed = ({ event, round }: { event: BseDto; round?: NegotiationRoundDto }): boolean => {
  if (round) return moment().isAfter(moment(round.deadline));
  return moment().isAfter(moment(event.publishedFields?.deadline ?? event.awardedFields?.deadline));
};

export const BidEvaluation = ({ sourcingEvent }: { sourcingEvent: BseDto }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const authState = useLoggedInWithOrgContextState();

  const { productGroups } = useProductGroups(sourcingEvent.id);

  const [completeEvent] = useCompleteEvaluationMutation();
  const [openCompletedEvent] = useOpenCompletedEventMutation();

  const [completeNegotiationRound] = useCompleteNegotiationRoundEvaluationMutation();
  const [openCompletedNegotiationRound] = useOpenNegotiationRoundMutation();
  const { currentRound, roundNumber } = useNegotiation();

  const bidSelection = useSelectProductBids(sourcingEvent);
  const {
    selectAllBidsFromSupplier,
    hasPendingChanges,
    lastSavedLabel,
    saveNow,
    isSaving,
  } = bidSelection;

  const {
    data: bidsResponse,
    error,
    isLoading,
  } = useListReceivedBidsQuery({ eventId: sourcingEvent.id, negotiationRound: currentRound?.id });

  const bids = useMemo(() => bidsResponse?.bids ?? [], [bidsResponse?.bids]);

  const { data: originalBidsResponse } = useListReceivedBidsQuery({ eventId: sourcingEvent.id });
  const originalBidsByOrgId = useMemo(() => {
    const map = new Map<string, ReceivedBseBidDto>();
    originalBidsResponse?.bids?.forEach((b) => map.set(b.owningOrganization.id, b));
    return map;
  }, [originalBidsResponse?.bids]);

  const selectedBids = getSelectedOrWinningBids(sourcingEvent, currentRound);

  const [isToggleLoading, setIsToggleLoading] = useState(false);

  const [selectedModel, setSelectedModel] = useState<EvaluationModel>();
  const [evaluationResult] = useState<EvaluationResult>();

  const completeEvaluation = async () => {
    setIsToggleLoading(true);
    try {
      if (currentRound) {
        const round = await completeNegotiationRound({
          eventId: sourcingEvent.id,
          negotiationId: currentRound.id,
        }).unwrap();
        dispatch(setNegotiationRound(round));
      } else {
        const event = await completeEvent({ eventId: sourcingEvent.id }).unwrap();
        dispatch(setSourcingEvent(event));
      }
    } finally {
      setIsToggleLoading(false);
    }
  };

  const openCompleted = async () => {
    setIsToggleLoading(true);
    try {
      if (currentRound) {
        const round = await openCompletedNegotiationRound({
          eventId: sourcingEvent.id,
          negotiationId: currentRound.id,
        }).unwrap();
        dispatch(setNegotiationRound(round));
      } else {
        const event = await openCompletedEvent({ eventId: sourcingEvent.id }).unwrap();
        dispatch(setSourcingEvent(event));
      }
    } finally {
      setIsToggleLoading(false);
    }
  };

  const getChangeDisplay = ({
    percentChange,
  }: {
    percentChange?: number;
  }): { color?: string; ArrowIcon?: IconType; formattedPercent?: string } => {
    if (percentChange == null) return {};
    const color = percentChange > 0 ? "red.500" : percentChange < 0 ? "green.500" : "smMuted";
    const ArrowIcon: IconType | undefined = percentChange > 0 ? FaArrowUp : percentChange < 0 ? FaArrowDown : undefined;
    const formattedPercent = `${percentChange > 0 ? "+" : ""}${percentChange.toFixed(1)}%`;
    return { color, ArrowIcon, formattedPercent };
  };

  const [productConversation, setProductConversation] = useState<{
    conversation: Message[];
    title: string;
    productGroup: ProductGroup;
    product: Product | ProductDraft;
  }>();

  const [orgIdToView, setOrgIdToView] = useState<string>();

  const handleComment = async ({
    group,
    product,
    comment,
  }: {
    group: ProductGroup;
    product: Product | ProductDraft;
    comment: string;
  }) => {
    const updatedGroup = cloneDeep(group);
    if (!updatedGroup.products) throw Error("No products found - could not handle comment.");
    const productIndex = updatedGroup.products.findIndex((p) => p.id === product.id);
    if (productIndex && productIndex < 0) throw Error(`Product w id=${product.id} not found.`);
    const updatedProduct = updatedGroup.products[productIndex];
    const message = {
      comment,
      createdAt: moment().toISOString(),
      modifiedAt: moment().toISOString(),
      createdBy: authState.me,
    };
    productConversation?.conversation.push({
      id: `${productConversation.conversation.length}`,
      value: message.comment,
      createdAt: message.createdAt,
      createdBy: `${message.createdBy.firstName} ${message.createdBy.lastName}`,
    });
    updatedProduct?.conversation?.push(message);
    dispatch(updateProductGroup(updatedGroup));
    await dispatch(
      editProductGroupThunk({
        id: group.id,
        editProduct: {
          id: updatedProduct.id,
          comment: { comment },
        },
      }),
    );
  };

  const handleGenerateReport = useCallback(() => {
    generateReport(productGroups, bids, sourcingEvent, currentRound);
  }, [bids, productGroups, sourcingEvent, currentRound]);

  const handleDownloadAllPrices = useCallback(() => {
    generatePriceComparisonReport(productGroups, bids, sourcingEvent);
  }, [bids, productGroups, sourcingEvent]);

  const handleSelectBid = useCallback(
    (bidId: string) => {
      const bid = bids?.find((b) => b.id === bidId);
      if (bid) {
        selectAllBidsFromSupplier(bid);
      }
    },
    [bids, selectAllBidsFromSupplier],
  );

  if (isLoading)
    return (
      <Flex width="full" height="full" justifyContent="center" alignItems="center">
        <Spinner />
      </Flex>
    );

  if (error) return <p>{t("Something went wrong, our team is on it!")}</p>;

  if (!bids?.length)
    return (
      <Text color="smMuted" textAlign="center" pt="8">
        {t("No bids delivered for this event")}.
      </Text>
    );

  return (
    <BidSelectionProvider value={bidSelection}>
      <ConversationModal
        isOpen={!!productConversation}
        title={productConversation?.title}
        conversation={productConversation?.conversation.map((msg) => ({
          id: msg.id,
          value: msg.value,
          createdBy: msg.createdBy,
          createdAt: moment(msg.createdAt).format("DD MMM YY hh.mm"),
        }))}
        onClose={() => setProductConversation(undefined)}
        handleMessage={(comment: string) => {
          if (!productConversation) return;
          handleComment({ comment, product: productConversation.product, group: productConversation.productGroup });
        }}
        isLoading={false}
        canComment={sourcingEvent.canEdit}
      />
      {orgIdToView && (
        <ViewBidAsBuyerModal
          eventId={sourcingEvent.id}
          organizationId={orgIdToView}
          onClose={() => setOrgIdToView(undefined)}
        />
      )}
      {requireBooleanEnvVar("VITE_ENABLE_ADVANCED_EVALUATION") && (
        <VStack spacing={5} align="stretch">
          <Box>
            <ModelSelector
              selectedModelType={selectedModel?.type}
              onSelectModel={setSelectedModel}
              disabledModels={[EvaluationModelType.QUALITY_ADJUSTED, EvaluationModelType.PRICE_QUALITY]}
            />
          </Box>

          {selectedModel?.type === EvaluationModelType.QUALITATIVE_AI && (
            <AIEvaluator
              sourcingEventId={sourcingEvent.id}
              productGroups={productGroups}
              setOrgIdToView={setOrgIdToView}
            />
          )}

          {evaluationResult && selectedModel?.type !== EvaluationModelType.LOWEST_PRICE && (
            <Box mt={4}>
              <Divider mb={6} />
              <Heading size="md" mb={4}>
                {t("Evaluation Results")}
              </Heading>
              <EvaluationResults
                result={evaluationResult}
                onSelectBid={handleSelectBid}
                modelType={selectedModel?.type}
              />
            </Box>
          )}
        </VStack>
      )}
      {productGroups?.length &&
      (!requireBooleanEnvVar("VITE_ENABLE_ADVANCED_EVALUATION") ||
        selectedModel?.type === EvaluationModelType.LOWEST_PRICE) ? (
        <>
          <FormControl display="flex" justifyContent="center" alignItems={"center"} flexDirection={"column"} py="10">
            <FormLabel htmlFor="email-alerts" m="0">
              {t("Complete Evaluation")}
            </FormLabel>
            <Flex align="center" mt="2" position="relative" justifyContent="center" minHeight="8">
              <Switch
                size={"lg"}
                disabled={
                  isToggleLoading ||
                  eventIsAwarded(sourcingEvent) ||
                  eventIsCancelled(sourcingEvent) ||
                  eventIsClosed(sourcingEvent) ||
                  !isDeadlinePassed({ event: sourcingEvent, round: currentRound })
                }
                isChecked={eventIsCompleted({ event: sourcingEvent, round: currentRound })}
                onChange={async (e) => {
                  if (e.target.checked) {
                    completeEvaluation();
                  } else {
                    openCompleted();
                  }
                }}
              />
              {isToggleLoading && (
                <Box position="absolute" right="-8" display="flex" alignItems="center" justifyContent="center">
                  <Spinner size="sm" color="smPrimary" />
                </Box>
              )}
            </Flex>
            <FormHelperText pb="8">
              {eventIsCompleted({ event: sourcingEvent, round: currentRound })
                ? t("The evaluation is completed")
                : t("Toggle to complete the evaluation with the current price selection")}
            </FormHelperText>
          </FormControl>
          <Box overflowX="auto">
          <Table>
            <Thead>
              <Tr>
                <Th p="1"></Th>
                {bids.map((bid) => (
                  <Th key={bid.id} p="1" textAlign={"center"}>
                    {bid.owningOrganization.name}
                  </Th>
                ))}
                <Th textAlign={"center"}>{t("Best prices")}</Th>
                <Th textAlign={"center"}>{t("Selected")}</Th>
              </Tr>
              {productGroups && (
                <>
                  <Tr fontWeight={"bold"}>
                    <Td>{t("SUM Total")}</Td>
                    {bids.map((bid) => {
                      const totalPrice = getTotalPrice({ bid, groups: productGroups });

                      return (
                        <Td
                          key={bid.id}
                          textAlign={"center"}
                          cursor={"pointer"}
                          color={isBestTotalPrice({ bid, bids, groups: productGroups }) ? "green.500" : "yellow.500"}
                          onClick={() => selectAllBidsFromSupplier(bid)}
                        >
                          {totalPrice?.toLocaleString()}
                        </Td>
                      );
                    })}
                    <Td textAlign={"center"}>
                      {getSumOfBestPrices({ groups: productGroups, bids })?.toLocaleString()}
                    </Td>
                    <Td textAlign={"center"}>
                      {getSumOfSelectedPrices({
                        groups: productGroups,
                        bids,
                        selectedBids,
                      }).toLocaleString()}
                    </Td>
                  </Tr>
                  {roundNumber > 0 && (
                    <Tr>
                      <Td>{t("Change since original bid")}</Td>
                      {bids.map((bid) => {
                        const originalBid = originalBidsByOrgId.get(bid.owningOrganization.id);
                        const percentChange = getBidPercentChangeSinceOriginal({
                          currentBid: bid,
                          originalBid,
                          groups: productGroups,
                        });
                        const { color, ArrowIcon, formattedPercent } = getChangeDisplay({ percentChange });
                        const absoluteChange =
                          originalBid &&
                          calculateChangeSinceOriginalBid({
                            currentBid: bid,
                            originalBid,
                            groups: productGroups,
                          });
                        const formattedAbsolute =
                          typeof absoluteChange === "number"
                            ? `${absoluteChange > 0 ? "+" : ""}${absoluteChange.toLocaleString()}`
                            : undefined;

                        return (
                          <Td key={bid.id} textAlign={"center"}>
                            {formattedPercent ? (
                              <Text fontSize="xs" color={color} display="inline-flex" alignItems="center" gap="1">
                                {ArrowIcon && <Icon as={ArrowIcon} />}
                                {formattedPercent}
                                {formattedAbsolute && <Text as="span">({formattedAbsolute})</Text>}
                              </Text>
                            ) : (
                              <Text fontSize="xs" color="smMuted">
                                {t("n/a")}
                              </Text>
                            )}
                          </Td>
                        );
                      })}
                      <Td></Td>
                      <Td></Td>
                    </Tr>
                  )}
                  <Tr>
                    <Td py="1">{t("Product quotes")}</Td>
                    {bids.map((bid) => (
                      <Td
                        key={bid.id}
                        textAlign={"center"}
                        color={
                          getQuotesDeliveredInBid({ bid }) >= getQuotesRequested(productGroups)
                            ? "green.500"
                            : "yellow.500"
                        }
                      >
                        {getQuotesDeliveredInBid({ bid })}/{getQuotesRequested(productGroups)}
                      </Td>
                    ))}
                    <Td
                      textAlign={"center"}
                      color={
                        getQuotesDelivered({ groups: productGroups, bids }) >= getQuotesRequested(productGroups)
                          ? "green.500"
                          : "yellow.500"
                      }
                    >
                      {getQuotesDelivered({ groups: productGroups, bids })}/{getQuotesRequested(productGroups)}
                    </Td>
                    <Td></Td>
                  </Tr>
                  <Tr>
                    <Td></Td>
                    {bids.map((bid) => (
                      <Td key={bid.id} textAlign={"center"} py="1">
                        <Button
                          size="sm"
                          colorScheme="teal"
                          variant="ghost"
                          leftIcon={<Icon as={FaEye} />}
                          onClick={() => setOrgIdToView(bid.owningOrganization.id)}
                        >
                          {t("View bid")}
                        </Button>
                      </Td>
                    ))}
                    <Td></Td>
                    <Td></Td>
                  </Tr>
                </>
              )}
            </Thead>
          </Table>
          </Box>
          <Box rounded="lg" p="4" mt="20">
            <BidSelectionSaveStatus
              hasPendingChanges={hasPendingChanges}
              lastSavedLabel={lastSavedLabel}
              onSaveNow={saveNow}
              isSaving={isSaving}
            />
            <Box overflow={"auto"}>
              {productGroups?.map((group) => (
                <Box key={group.id}>
                  <EvaluationTable
                    sourcingEvent={sourcingEvent}
                    bids={bids}
                    group={group}
                    setProductConversation={setProductConversation}
                  />
                </Box>
              ))}
            </Box>
          </Box>
          <Flex justifyContent={"end"} pb="4" pr="4" gap="2">
            <Button colorScheme="teal" variant={"outline"} onClick={handleDownloadAllPrices}>
              {t("Download all prices")} <Icon as={FaDownload} ml="2" />
            </Button>
            {(eventIsCompleted({ event: sourcingEvent, round: currentRound }) ||
              authState.selectedOrg.canSeeDeliveredBidsBeforeDeadline) && (
              <Button colorScheme="teal" variant={"outline"} onClick={handleGenerateReport}>
                {t("Report")} <Icon as={FaDownload} ml="2" />
              </Button>
            )}
          </Flex>
        </>
      ) : null}
      {!productGroups?.length && (
        <Text color="smMuted" textAlign="center" pt="8">
          {t("There are no products in this sourcing event")}.
        </Text>
      )}
    </BidSelectionProvider>
  );
};
